/** @forge-header
 * @id          polyHydration
 * @role        Polymarket cross-venue hydration | slug resolution + CLOB streams for moneyline/spread/total
 * @pattern     orchestrator
 * @purity      ws,api
 * @units       cents
 * @upstream    sportsDiscovery:groupMarketsByEvent,DiscoveredMarket | polymarket/marketStream:createPolyMarketStream | polymarket/normalize:*
 * @downstream  sportsStream/stream (background hydration)
 * @exports     hydratePolyMoneylines:fn | hydratePolySpreads:fn | hydratePolyTotals:fn | PolyTokenMapping:type | PolyHydrationDeps:type
 * @test        stream.test.ts (integration-level via createSportsStream)
 */

import type { Sport } from '../sportsDiscovery';
import { groupMarketsByEvent, type DiscoveredMarket } from '../sportsDiscovery';
import { createPolyMarketStream } from '../polymarket/marketStream';
import {
  makePolyGameSlug,
  resolvePolyGameSlugByParticipants,
  resolvePolyMarketInfoBySlug,
  resolvePolyTennisGameSlug,
  resolvePolySpreadMarketsByGameSlug,
  type PolySpreadMarketInfo,
  resolvePolyTotalMarketsByGameSlug,
  type PolyTotalMarketInfo,
  clearGammaEventsCache,
} from '../polymarket/gamma';
import { polyAsksAsNoLevels, polyBidsAsYesLevels } from '../polymarket/normalize';
import type {
  GameData,
  MoneylineGameData,
  SpreadGameData,
  TotalGameData,
  MarketBook,
  SportsLoadPhase,
  SportsLoadEvent,
} from './types';
import { getBestBid } from './types';

/** Token-to-event mapping for poly stream book handlers */
export type PolyTokenMapping = {
  eventTicker: string;
  side: 'away' | 'home' | 'favorite' | 'underdog' | 'over' | 'under';
};

/** Dependencies injected from the parent stream closure */
export interface PolyHydrationDeps {
  sport: Sport;
  marketType: 'moneyline' | 'spread' | 'total';
  useRelay: boolean;
  discoveredMarkets: DiscoveredMarket[];
  gamesByEvent: Map<string, GameData>;
  polyTokenToEvent: Map<string, PolyTokenMapping>;
  polyMoneylineByEvent: Map<string, NonNullable<MoneylineGameData['polymarket']>>;
  polySpreadByEvent: Map<string, NonNullable<SpreadGameData['polymarketMain']>>;
  polyTotalByEvent: Map<string, NonNullable<TotalGameData['polymarketMain']>>;
  emit: () => void;
  updateLoadingState: (
    phase: SportsLoadPhase,
    args?: {
      message?: string;
      done?: number;
      total?: number;
      level?: SportsLoadEvent['level'];
    }
  ) => void;
  getLoadingPhase: () => SportsLoadPhase;
  setLastUpdateMs: (ms: number) => void;
}

// ---------------------------------------------------------------------------
// Picker helpers (pure functions)
// ---------------------------------------------------------------------------

export function pickMainKalshiSpread(
  game: SpreadGameData
): { floorStrike: number; favoriteCode: string } | null {
  let best: { floorStrike: number; favoriteCode: string } | null = null;
  let minDiff = Infinity;
  for (const [, info] of game.markets) {
    const yesBid = getBestBid(info.book, 'yes');
    if (yesBid.priceCents === null) continue;
    const diff = Math.abs(yesBid.priceCents - 50);
    if (diff < minDiff) {
      minDiff = diff;
      best = {
        floorStrike: info.floorStrike,
        favoriteCode: info.teamCode,
      };
    }
  }
  return best;
}

export function pickPolySpreadCandidate(
  candidates: PolySpreadMarketInfo[],
  desiredFavoriteSide: 'away' | 'home',
  desiredLine: number
): PolySpreadMarketInfo | null {
  let pool = candidates.filter((c) => c.favoriteSide === desiredFavoriteSide);
  if (pool.length === 0) pool = candidates;
  if (pool.length === 0) return null;
  let best = pool[0] ?? null;
  let minDiff = best ? Math.abs(best.spreadValue - desiredLine) : Infinity;
  for (const c of pool) {
    const diff = Math.abs(c.spreadValue - desiredLine);
    if (diff < minDiff) {
      minDiff = diff;
      best = c;
    }
  }
  return best;
}

export function pickMainKalshiTotal(game: TotalGameData): number | null {
  let line: number | null = null;
  let minDiff = Infinity;
  for (const [strike, book] of game.markets) {
    const yesBid = getBestBid(book, 'yes');
    if (yesBid.priceCents === null) continue;
    const diff = Math.abs(yesBid.priceCents - 50);
    if (diff < minDiff) {
      minDiff = diff;
      line = strike;
    }
  }
  return line;
}

export function pickPolyTotalCandidate(
  candidates: PolyTotalMarketInfo[],
  desiredLine: number
): PolyTotalMarketInfo | null {
  if (candidates.length === 0) return null;
  let best = candidates[0] ?? null;
  let minDiff = best ? Math.abs(best.totalLine - desiredLine) : Infinity;
  for (const c of candidates) {
    const diff = Math.abs(c.totalLine - desiredLine);
    if (diff < minDiff) {
      minDiff = diff;
      best = c;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Hydration functions
// ---------------------------------------------------------------------------

/** Hydrate Polymarket moneyline data. Returns the created poly stream (or null). */
export async function hydratePolyMoneylines(
  deps: PolyHydrationDeps
): Promise<ReturnType<typeof createPolyMarketStream> | null> {
  const {
    sport,
    marketType,
    useRelay,
    discoveredMarkets,
    gamesByEvent,
    polyTokenToEvent,
    polyMoneylineByEvent,
    emit,
    updateLoadingState,
    getLoadingPhase,
    setLastUpdateMs,
  } = deps;

  if (marketType !== 'moneyline') return null;
  polyTokenToEvent.clear();
  polyMoneylineByEvent.clear();
  clearGammaEventsCache();

  const grouped = groupMarketsByEvent(discoveredMarkets);
  const tokenIds: string[] = [];
  const totalEvents = grouped.size;
  let resolvedEvents = 0;
  updateLoadingState('hydrating-polymarket', {
    message: 'Resolving Polymarket moneylines',
    done: 0,
    total: totalEvents,
  });
  emit();

  const resolves = Array.from(grouped.values()).map(async (event) => {
    try {
      let slug = makePolyGameSlug({
        sport,
        dateYyyyMmDd: event.dateYyyyMmDd,
        awayCode: event.awayCode,
        homeCode: event.homeCode,
      });

      let info = slug
        ? await resolvePolyMarketInfoBySlug({
            slug,
            useRelay,
            awayName: event.awayName,
            homeName: event.homeName,
          })
        : null;
      if (
        !info &&
        (sport === 'cbb' || sport === 'wcbb' || sport === 'tennis-atp' || sport === 'tennis-wta')
      ) {
        const resolved = await resolvePolyGameSlugByParticipants({
          sport,
          dateYyyyMmDd: event.dateYyyyMmDd,
          awayName: event.awayName,
          homeName: event.homeName,
          useRelay,
        });
        if (resolved) {
          slug = resolved;
          info = await resolvePolyMarketInfoBySlug({
            slug,
            useRelay,
            awayName: event.awayName,
            homeName: event.homeName,
          });
        }
      }
      return { eventTicker: event.eventTicker, info };
    } catch (err) {
      console.warn(`Polymarket moneyline resolve failed for ${event.eventTicker}:`, err);
      return { eventTicker: event.eventTicker, info: null };
    } finally {
      resolvedEvents += 1;
      updateLoadingState('hydrating-polymarket', {
        done: resolvedEvents,
        total: totalEvents,
      });
      emit();
    }
  });

  const selected = await Promise.all(resolves);
  for (const rec of selected) {
    if (!rec.info) continue;
    const game = gamesByEvent.get(rec.eventTicker);
    if (!game || game.marketType !== 'moneyline') continue;

    const awayBook: MarketBook = {
      marketTicker: `asset:${rec.info.awayTokenId}`,
      tokenId: rec.info.awayTokenId,
      conditionId: rec.info.conditionId,
      tickSize: rec.info.tickSize,
      negRisk: rec.info.negRisk,
      yes:
        typeof rec.info.awayPriceCents === 'number'
          ? [{ priceCents: rec.info.awayPriceCents, size: 1 }]
          : [],
      no:
        typeof rec.info.awayPriceCents === 'number'
          ? [{ priceCents: 100 - rec.info.awayPriceCents, size: 1 }]
          : [],
    };
    const homeBook: MarketBook = {
      marketTicker: `asset:${rec.info.homeTokenId}`,
      tokenId: rec.info.homeTokenId,
      conditionId: rec.info.conditionId,
      tickSize: rec.info.tickSize,
      negRisk: rec.info.negRisk,
      yes:
        typeof rec.info.homePriceCents === 'number'
          ? [{ priceCents: rec.info.homePriceCents, size: 1 }]
          : [],
      no:
        typeof rec.info.homePriceCents === 'number'
          ? [{ priceCents: 100 - rec.info.homePriceCents, size: 1 }]
          : [],
    };

    const polyMoneyline: NonNullable<MoneylineGameData['polymarket']> = {
      eventId: rec.info.slug,
      liquidityUsd: rec.info.liquidityUsd,
      markets: {
        away: awayBook,
        home: homeBook,
      },
    };
    polyMoneylineByEvent.set(rec.eventTicker, polyMoneyline);
    game.polymarket = polyMoneyline;

    polyTokenToEvent.set(rec.info.awayTokenId, {
      eventTicker: rec.eventTicker,
      side: 'away',
    });
    polyTokenToEvent.set(rec.info.homeTokenId, {
      eventTicker: rec.eventTicker,
      side: 'home',
    });
    tokenIds.push(rec.info.awayTokenId, rec.info.homeTokenId);
  }

  if (tokenIds.length === 0) {
    updateLoadingState('hydrating-polymarket', {
      message: 'No Polymarket moneylines matched',
      done: resolvedEvents,
      total: totalEvents,
      level: 'warn',
    });
    emit();
    return null;
  }

  const polyStream = createPolyMarketStream();
  polyStream.onError((e) => {
    console.warn(`Polymarket moneyline stream error (${sport} ${marketType}):`, e);
    updateLoadingState(getLoadingPhase(), {
      message: 'Polymarket stream error',
      level: 'error',
    });
    emit();
  });
  polyStream.onBook((snap) => {
    const mapping = polyTokenToEvent.get(snap.assetId);
    if (!mapping) return;
    const poly = polyMoneylineByEvent.get(mapping.eventTicker);
    if (!poly) return;

    const book = mapping.side === 'away' ? poly.markets.away : poly.markets.home;
    book.yes = polyBidsAsYesLevels(snap.bids);
    book.no = polyAsksAsNoLevels(snap.asks);
    book.tsMs = snap.tsMs;
    poly.tsMs = snap.tsMs;
    const game = gamesByEvent.get(mapping.eventTicker);
    if (game && game.marketType === 'moneyline') {
      game.polymarket = poly;
    }
    setLastUpdateMs(Date.now());
    emit();
  });

  await polyStream.connect();
  polyStream.subscribe(Array.from(new Set(tokenIds)));
  return polyStream;
}

/** Hydrate Polymarket spread data. Returns the created poly stream (or null). */
export async function hydratePolySpreads(
  deps: PolyHydrationDeps
): Promise<ReturnType<typeof createPolyMarketStream> | null> {
  const {
    sport,
    marketType,
    useRelay,
    discoveredMarkets,
    gamesByEvent,
    polyTokenToEvent,
    polySpreadByEvent,
    emit,
    updateLoadingState,
    getLoadingPhase,
    setLastUpdateMs,
  } = deps;

  if (marketType !== 'spread') return null;
  if (sport !== 'nfl' && sport !== 'nba' && sport !== 'tennis-atp' && sport !== 'tennis-wta')
    return null;
  polyTokenToEvent.clear();
  polySpreadByEvent.clear();
  clearGammaEventsCache();

  const grouped = groupMarketsByEvent(discoveredMarkets);
  const tokenIds: string[] = [];
  const totalEvents = grouped.size;
  let resolvedEvents = 0;
  updateLoadingState('hydrating-polymarket', {
    message: 'Resolving Polymarket spreads',
    done: 0,
    total: totalEvents,
  });
  emit();

  const resolves = Array.from(grouped.values()).map(async (event) => {
    try {
      const isTennis = sport === 'tennis-atp' || sport === 'tennis-wta';
      let slug = isTennis
        ? null
        : makePolyGameSlug({
            sport,
            dateYyyyMmDd: event.dateYyyyMmDd,
            awayCode: event.awayCode,
            homeCode: event.homeCode,
          });
      if (isTennis) {
        slug = await resolvePolyTennisGameSlug({
          sport,
          dateYyyyMmDd: event.dateYyyyMmDd,
          awayName: event.awayName,
          homeName: event.homeName,
          useRelay,
        });
      }
      if (!slug) {
        return { eventTicker: event.eventTicker, candidate: null as PolySpreadMarketInfo | null };
      }

      const game = gamesByEvent.get(event.eventTicker);
      if (!game || game.marketType !== 'spread') {
        return { eventTicker: event.eventTicker, candidate: null as PolySpreadMarketInfo | null };
      }

      const mainKalshi = pickMainKalshiSpread(game);
      const desiredFavoriteSide =
        mainKalshi && mainKalshi.favoriteCode.toUpperCase() === event.awayCode.toUpperCase()
          ? ('away' as const)
          : ('home' as const);
      const desiredLine = mainKalshi?.floorStrike ?? 0;

      const candidates = await resolvePolySpreadMarketsByGameSlug({ slug, useRelay });
      const candidate = pickPolySpreadCandidate(candidates, desiredFavoriteSide, desiredLine);
      return { eventTicker: event.eventTicker, candidate };
    } catch (err) {
      console.warn(`Polymarket spread resolve failed for ${event.eventTicker}:`, err);
      return { eventTicker: event.eventTicker, candidate: null as PolySpreadMarketInfo | null };
    } finally {
      resolvedEvents += 1;
      updateLoadingState('hydrating-polymarket', {
        done: resolvedEvents,
        total: totalEvents,
      });
      emit();
    }
  });

  const selected = await Promise.all(resolves);
  for (const rec of selected) {
    if (!rec.candidate) continue;
    const game = gamesByEvent.get(rec.eventTicker);
    if (!game || game.marketType !== 'spread') continue;

    const favoriteBook: MarketBook = {
      marketTicker: `asset:${rec.candidate.favoriteTokenId}`,
      tokenId: rec.candidate.favoriteTokenId,
      conditionId: rec.candidate.conditionId,
      tickSize: rec.candidate.tickSize,
      negRisk: rec.candidate.negRisk,
      yes:
        typeof rec.candidate.favoritePriceCents === 'number'
          ? [{ priceCents: rec.candidate.favoritePriceCents, size: 1 }]
          : [],
      no:
        typeof rec.candidate.favoritePriceCents === 'number'
          ? [{ priceCents: 100 - rec.candidate.favoritePriceCents, size: 1 }]
          : [],
    };
    const underdogBook: MarketBook = {
      marketTicker: `asset:${rec.candidate.underdogTokenId}`,
      tokenId: rec.candidate.underdogTokenId,
      conditionId: rec.candidate.conditionId,
      tickSize: rec.candidate.tickSize,
      negRisk: rec.candidate.negRisk,
      yes:
        typeof rec.candidate.underdogPriceCents === 'number'
          ? [{ priceCents: rec.candidate.underdogPriceCents, size: 1 }]
          : [],
      no:
        typeof rec.candidate.underdogPriceCents === 'number'
          ? [{ priceCents: 100 - rec.candidate.underdogPriceCents, size: 1 }]
          : [],
    };

    const polyMain: NonNullable<SpreadGameData['polymarketMain']> = {
      line: rec.candidate.spreadValue,
      favoriteSide: rec.candidate.favoriteSide,
      liquidityUsd: rec.candidate.liquidityUsd,
      markets: {
        favorite: favoriteBook,
        underdog: underdogBook,
      },
    };
    polySpreadByEvent.set(rec.eventTicker, polyMain);
    game.polymarketMain = polyMain;

    polyTokenToEvent.set(rec.candidate.favoriteTokenId, {
      eventTicker: rec.eventTicker,
      side: 'favorite',
    });
    polyTokenToEvent.set(rec.candidate.underdogTokenId, {
      eventTicker: rec.eventTicker,
      side: 'underdog',
    });
    tokenIds.push(rec.candidate.favoriteTokenId, rec.candidate.underdogTokenId);
  }

  if (tokenIds.length === 0) return null;

  const polyStream = createPolyMarketStream();
  polyStream.onError((e) => {
    console.warn(`Polymarket spread stream error (${sport} ${marketType}):`, e);
    updateLoadingState(getLoadingPhase(), {
      message: 'Polymarket stream error',
      level: 'error',
    });
    emit();
  });
  polyStream.onBook((snap) => {
    const mapping = polyTokenToEvent.get(snap.assetId);
    if (!mapping) return;
    const polyMain = polySpreadByEvent.get(mapping.eventTicker);
    if (!polyMain) return;

    const book =
      mapping.side === 'favorite' ? polyMain.markets.favorite : polyMain.markets.underdog;
    book.yes = polyBidsAsYesLevels(snap.bids);
    book.no = polyAsksAsNoLevels(snap.asks);
    book.tsMs = snap.tsMs;
    const game = gamesByEvent.get(mapping.eventTicker);
    if (game && game.marketType === 'spread') {
      game.polymarketMain = polyMain;
    }
    setLastUpdateMs(Date.now());
    emit();
  });

  await polyStream.connect();
  polyStream.subscribe(Array.from(new Set(tokenIds)));
  return polyStream;
}

/** Hydrate Polymarket total data. Returns the created poly stream (or null). */
export async function hydratePolyTotals(
  deps: PolyHydrationDeps
): Promise<ReturnType<typeof createPolyMarketStream> | null> {
  const {
    sport,
    marketType,
    useRelay,
    discoveredMarkets,
    gamesByEvent,
    polyTokenToEvent,
    polyTotalByEvent,
    emit,
    updateLoadingState,
    getLoadingPhase,
    setLastUpdateMs,
  } = deps;

  if (marketType !== 'total') return null;
  if (sport !== 'nba' && sport !== 'nfl' && sport !== 'tennis-atp' && sport !== 'tennis-wta')
    return null;
  polyTokenToEvent.clear();
  polyTotalByEvent.clear();
  clearGammaEventsCache();

  const grouped = groupMarketsByEvent(discoveredMarkets);
  const tokenIds: string[] = [];
  const totalEvents = grouped.size;
  let resolvedEvents = 0;
  updateLoadingState('hydrating-polymarket', {
    message: 'Resolving Polymarket totals',
    done: 0,
    total: totalEvents,
  });
  emit();

  const resolves = Array.from(grouped.values()).map(async (event) => {
    try {
      const isTennis = sport === 'tennis-atp' || sport === 'tennis-wta';
      let slug = isTennis
        ? null
        : makePolyGameSlug({
            sport,
            dateYyyyMmDd: event.dateYyyyMmDd,
            awayCode: event.awayCode,
            homeCode: event.homeCode,
          });
      if (isTennis) {
        slug = await resolvePolyTennisGameSlug({
          sport,
          dateYyyyMmDd: event.dateYyyyMmDd,
          awayName: event.awayName,
          homeName: event.homeName,
          useRelay,
        });
      }
      if (!slug) {
        return { eventTicker: event.eventTicker, candidate: null as PolyTotalMarketInfo | null };
      }

      const game = gamesByEvent.get(event.eventTicker);
      if (!game || game.marketType !== 'total') {
        return { eventTicker: event.eventTicker, candidate: null as PolyTotalMarketInfo | null };
      }

      const desiredLine = pickMainKalshiTotal(game) ?? 0;
      const candidates = await resolvePolyTotalMarketsByGameSlug({ slug, useRelay });
      const candidate = pickPolyTotalCandidate(candidates, desiredLine);
      return { eventTicker: event.eventTicker, candidate };
    } catch (err) {
      console.warn(`Polymarket total resolve failed for ${event.eventTicker}:`, err);
      return { eventTicker: event.eventTicker, candidate: null as PolyTotalMarketInfo | null };
    } finally {
      resolvedEvents += 1;
      updateLoadingState('hydrating-polymarket', {
        done: resolvedEvents,
        total: totalEvents,
      });
      emit();
    }
  });

  const selected = await Promise.all(resolves);
  for (const rec of selected) {
    if (!rec.candidate) continue;
    const game = gamesByEvent.get(rec.eventTicker);
    if (!game || game.marketType !== 'total') continue;

    const overBook: MarketBook = {
      marketTicker: `asset:${rec.candidate.overTokenId}`,
      tokenId: rec.candidate.overTokenId,
      conditionId: rec.candidate.conditionId,
      tickSize: rec.candidate.tickSize,
      negRisk: rec.candidate.negRisk,
      yes:
        typeof rec.candidate.overPriceCents === 'number'
          ? [{ priceCents: rec.candidate.overPriceCents, size: 1 }]
          : [],
      no:
        typeof rec.candidate.overPriceCents === 'number'
          ? [{ priceCents: 100 - rec.candidate.overPriceCents, size: 1 }]
          : [],
    };
    const underBook: MarketBook = {
      marketTicker: `asset:${rec.candidate.underTokenId}`,
      tokenId: rec.candidate.underTokenId,
      conditionId: rec.candidate.conditionId,
      tickSize: rec.candidate.tickSize,
      negRisk: rec.candidate.negRisk,
      yes:
        typeof rec.candidate.underPriceCents === 'number'
          ? [{ priceCents: rec.candidate.underPriceCents, size: 1 }]
          : [],
      no:
        typeof rec.candidate.underPriceCents === 'number'
          ? [{ priceCents: 100 - rec.candidate.underPriceCents, size: 1 }]
          : [],
    };

    const polyMain: NonNullable<TotalGameData['polymarketMain']> = {
      line: rec.candidate.totalLine,
      liquidityUsd: rec.candidate.liquidityUsd,
      markets: {
        over: overBook,
        under: underBook,
      },
    };
    polyTotalByEvent.set(rec.eventTicker, polyMain);
    game.polymarketMain = polyMain;

    polyTokenToEvent.set(rec.candidate.overTokenId, {
      eventTicker: rec.eventTicker,
      side: 'over',
    });
    polyTokenToEvent.set(rec.candidate.underTokenId, {
      eventTicker: rec.eventTicker,
      side: 'under',
    });
    tokenIds.push(rec.candidate.overTokenId, rec.candidate.underTokenId);
  }

  if (tokenIds.length === 0) return null;

  const polyStream = createPolyMarketStream();
  polyStream.onError((e) => {
    console.warn(`Polymarket total stream error (${sport} ${marketType}):`, e);
    updateLoadingState(getLoadingPhase(), {
      message: 'Polymarket stream error',
      level: 'error',
    });
    emit();
  });
  polyStream.onBook((snap) => {
    const mapping = polyTokenToEvent.get(snap.assetId);
    if (!mapping) return;
    const polyMain = polyTotalByEvent.get(mapping.eventTicker);
    if (!polyMain) return;

    const book = mapping.side === 'over' ? polyMain.markets.over : polyMain.markets.under;
    book.yes = polyBidsAsYesLevels(snap.bids);
    book.no = polyAsksAsNoLevels(snap.asks);
    book.tsMs = snap.tsMs;
    const game = gamesByEvent.get(mapping.eventTicker);
    if (game && game.marketType === 'total') {
      game.polymarketMain = polyMain;
    }
    setLastUpdateMs(Date.now());
    emit();
  });

  await polyStream.connect();
  polyStream.subscribe(Array.from(new Set(tokenIds)));
  return polyStream;
}
